import { Component } from '@theme/component';

const STORAGE_KEY = 'bella-lea:wishlist';

/**
 * @typedef {object} WishlistItem
 * @property {string} id
 * @property {string} handle
 * @property {string} title
 * @property {string} image
 * @property {string} price
 * @property {string} url
 */

/**
 * Dispatched on `document` whenever the wishlist contents change.
 */
export class WishlistUpdateEvent extends Event {
  static eventName = 'wishlist:update';

  /** @param {WishlistItem[]} items */
  constructor(items) {
    super(WishlistUpdateEvent.eventName, { bubbles: true });
    this.items = items;
  }
}

export const WishlistStore = {
  /** @returns {WishlistItem[]} */
  getAll() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (error) {
      console.warn('[wishlist] Could not read wishlist from storage:', error);
      return [];
    }
  },

  /** @param {string} id */
  has(id) {
    return this.getAll().some((item) => item.id === id);
  },

  /** @param {WishlistItem} item */
  toggle(item) {
    const items = this.getAll();
    const index = items.findIndex((existing) => existing.id === item.id);

    if (index > -1) {
      items.splice(index, 1);
    } else {
      items.push(item);
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new WishlistUpdateEvent(items));

    return items;
  },

  /** @param {string} id */
  remove(id) {
    const items = this.getAll().filter((item) => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new WishlistUpdateEvent(items));
    return items;
  },
};

/**
 * The header wishlist icon: shows a live count badge.
 *
 * @typedef {object} IconRefs
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistBubbleCount
 *
 * @extends {Component<IconRefs>}
 */
class WishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleCount'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
    this.renderCount(WishlistStore.getAll().length);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
  }

  /** @param {WishlistUpdateEvent} event */
  onWishlistUpdate = (event) => {
    this.renderCount(event.items.length);
  };

  /** @param {number} count */
  renderCount = (count) => {
    this.refs.wishlistBubbleCount.textContent = String(count);
  };
}

/**
 * A heart button that adds/removes a single product from the wishlist.
 * Reads the product snapshot from its own `data-*` attributes.
 *
 * @extends {Component}
 */
class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.syncState);
    this.syncState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.syncState);
  }

  get productId() {
    return this.dataset.productId ?? '';
  }

  toggle = () => {
    if (!this.productId) return;

    WishlistStore.toggle({
      id: this.productId,
      handle: this.dataset.productHandle ?? '',
      title: this.dataset.productTitle ?? '',
      image: this.dataset.productImage ?? '',
      price: this.dataset.productPrice ?? '',
      url: this.dataset.productUrl ?? '',
    });
  };

  syncState = () => {
    const active = this.productId ? WishlistStore.has(this.productId) : false;
    this.classList.toggle('wishlist-button--active', active);
    this.setAttribute('aria-pressed', active ? 'true' : 'false');
  };
}

/**
 * Renders the wishlist page content (empty state vs. product grid) from the stored items.
 *
 * @typedef {object} PageRefs
 * @property {HTMLElement} empty
 * @property {HTMLElement} grid
 *
 * @extends {Component<PageRefs>}
 */
class WishlistPage extends Component {
  requiredRefs = ['empty', 'grid'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
    this.render(WishlistStore.getAll());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(WishlistUpdateEvent.eventName, this.onWishlistUpdate);
  }

  /** @param {WishlistUpdateEvent} event */
  onWishlistUpdate = (event) => {
    this.render(event.items);
  };

  /** @param {WishlistItem[]} items */
  render = (items) => {
    this.refs.empty.classList.toggle('hidden', items.length > 0);
    this.refs.grid.classList.toggle('hidden', items.length === 0);
    this.refs.grid.replaceChildren(...items.map((item) => this.buildCard(item)));
  };

  /** @param {WishlistItem} item */
  buildCard = (item) => {
    const card = document.createElement('div');
    card.className = 'wishlist-card';

    const link = document.createElement('a');
    link.className = 'wishlist-card__link';
    link.href = item.url;

    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = item.title;
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      img.className = 'wishlist-card__image';
      link.append(img);
    }

    const title = document.createElement('p');
    title.className = 'wishlist-card__title';
    title.textContent = item.title;
    link.append(title);

    const price = document.createElement('p');
    price.className = 'wishlist-card__price';
    price.textContent = item.price;
    link.append(price);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'wishlist-card__remove';
    remove.setAttribute('on:click', '/removeItem');
    remove.dataset.productId = item.id;
    remove.setAttribute('aria-label', 'Von der Wunschliste entfernen');
    remove.textContent = '×';

    card.append(link, remove);
    return card;
  };

  /** @param {Event} event */
  removeItem = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const id = target.closest('[data-product-id]')?.dataset.productId;
    if (id) WishlistStore.remove(id);
  };
}

if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
